export type TabId = 'accueil' | 'projets' | 'finance' | 'moi';
export type PrimaryNavId = 'accueil' | 'opportunites' | 'missions' | 'finances' | 'documents' | 'profil' | 'create';
export type ExpertNavGroupId = 'missions' | 'pilotage' | 'documents' | 'memoire';

/**
 * Source unique de la navigation principale (mobile bottom nav + rail
 * desktop, guidé et expert). Toute nouvelle destination de premier niveau
 * doit être ajoutée ici et nulle part ailleurs — voir
 * docs/HUB-PRODUCT-RENEWAL-AUDIT.md (LOT 1) pour l'historique de cette
 * unification.
 */
export type PrimaryNavItem = {
  id: PrimaryNavId;
  label: string;
  mobileLabel?: string;
  icon: string;
  to: string;
  kind: 'nav' | 'action';
};

/**
 * Catalogue complet des outils. Chaque entrée déclare explicitement sa
 * destination de navigation principale (`primary`) — c'est la seule source
 * de vérité utilisée pour résoudre l'état actif de la nav (voir
 * `getPrimaryNavigationActiveId`). Le champ `tab` reste utilisé
 * indépendamment pour grouper les outils affichés à l'intérieur des écrans
 * Projets/Finance/Moi (voir `getToolsForTab`) : il ne pilote plus aucun
 * rendu de navigation de premier niveau.
 */
export type HubNavItem = {
  to: string;
  label: string;
  shortLabel?: string;
  icon: string;
  tab: TabId;
  primary: PrimaryNavId;
  description: string;
  access?: 'free' | 'starter' | 'pro' | 'owner';
  section?: string;
  parcours?: Array<'tech' | 'manuel' | 'reglemente'>;
  tier?: 'core' | 'advanced';
};

export type ExpertNavGroup = {
  id: ExpertNavGroupId;
  label: string;
  icon: string;
  description: string;
  items: ExpertNavLink[];
};

export type ExpertNavLink = {
  to: string;
  label: string;
  icon: string;
  description: string;
  access?: 'free' | 'starter' | 'pro' | 'owner';
};

/**
 * Les 6 destinations principales + l'action de création. Rendues à
 * l'identique en mode guidé et en mode expert (même tableau, même ordre) :
 * les deux modes partagent une seule et même structure de navigation, seul
 * le contenu additionnel (groupes experts, voir plus bas) diffère.
 */
export const PRIMARY_NAV_ITEMS: PrimaryNavItem[] = [
  { id: 'accueil', label: 'Accueil', icon: '🏠', to: '/accueil', kind: 'nav' },
  { id: 'opportunites', label: 'Opportunités', icon: '🎯', to: '/opportunites', kind: 'nav' },
  { id: 'missions', label: 'Missions', icon: '📁', to: '/missions', kind: 'nav' },
  { id: 'finances', label: 'Finances', icon: '📊', to: '/finance', kind: 'nav' },
  { id: 'documents', label: 'Documents', icon: '📄', to: '/documents', kind: 'nav' },
  { id: 'profil', label: 'Profil', icon: '👤', to: '/moi', kind: 'nav' },
  { id: 'create', label: 'Nouveau', mobileLabel: 'Créer', icon: '➕', to: '/missions/new', kind: 'action' },
];

/**
 * Barre mobile : exactement 5 destinations/actions, "Créer" au centre
 * (fondation mobile, Lot 1 — MOB-011). Remplace le compromis à 4 items de
 * LOT 1.1 (docs/HUB-LOT1.1-MOBILE-NAV.md, Phase C, qui reléguait
 * Opportunités au menu secondaire) : la nouvelle cible produit veut
 * Opportunités directement accessible dans la barre. Documents et Profil
 * restent dans le menu secondaire (`SECONDARY_MOBILE_NAV_ITEMS`, ouvert
 * depuis le bouton central "Créer").
 */
export const MOBILE_NAV_ITEMS: PrimaryNavItem[] = [
  PRIMARY_NAV_ITEMS.find((item) => item.id === 'accueil')!,
  PRIMARY_NAV_ITEMS.find((item) => item.id === 'opportunites')!,
  PRIMARY_NAV_ITEMS.find((item) => item.id === 'create')!,
  PRIMARY_NAV_ITEMS.find((item) => item.id === 'missions')!,
  PRIMARY_NAV_ITEMS.find((item) => item.id === 'finances')!,
];

/**
 * Destinations principales qui ne tiennent pas dans la barre mobile.
 * Dérivé de `PRIMARY_NAV_ITEMS` (même source, aucune configuration
 * dupliquée) : toute destination `kind: 'nav'` absente de `MOBILE_NAV_ITEMS`
 * apparaît ici automatiquement — impossible d'en perdre une par oubli.
 */
export const SECONDARY_MOBILE_NAV_ITEMS: PrimaryNavItem[] = PRIMARY_NAV_ITEMS.filter(
  (item) => item.kind === 'nav' && !MOBILE_NAV_ITEMS.some((mobileItem) => mobileItem.id === item.id),
);

export const HUB_NAV_ITEMS: HubNavItem[] = [
  // ── Onglets principaux ────────────────────────────────────
  { to: '/accueil', label: 'Accueil', icon: '📊', tab: 'accueil', primary: 'accueil', description: 'Vue rapide de votre activité',          access: 'free' },
  { to: '/projets', label: 'Projets', icon: '📋', tab: 'projets', primary: 'missions', description: 'Vos missions et leur avancement',       access: 'free' },
  { to: '/finance', label: 'Finance', icon: '💰', tab: 'finance', primary: 'finances', description: 'Argent, simulations, objectifs',        access: 'free' },
  { to: '/moi',     label: 'Moi',     icon: '👤', tab: 'moi',     primary: 'profil',   description: 'Profil, abonnement, réglages',         access: 'free' },

  // ── Projets ───────────────────────────────────────────────
  { to: '/tools/eval',      label: 'Évaluer une opportunité', shortLabel: 'Évaluer',     icon: '✨', tab: 'projets', primary: 'missions', description: 'Qualifier avant de vous engager (GO / NO-GO)',    access: 'free' },
  { to: '/tools/mission-canvas', label: 'Cadrage mission', shortLabel: 'Cadrage', icon: '🖼️', tab: 'projets', primary: 'missions', description: 'Définir clairement la mission avant de commencer',  access: 'starter' },
  { to: '/tools/sipoc',          label: 'Périmètre',       shortLabel: 'Périmètre', icon: '📋', tab: 'projets', primary: 'missions', description: 'Ce qui est inclus ou non dans la mission',     access: 'free', tier: 'advanced' },
  { to: '/tools/moscow',    label: 'Priorités',       shortLabel: 'Priorités', icon: '🎯', tab: 'projets', primary: 'missions', description: 'Décider ce qui est essentiel',         access: 'free', tier: 'advanced' },
  { to: '/tools/roles',     label: 'Rôles',           shortLabel: 'Rôles',     icon: '👥', tab: 'projets', primary: 'missions', description: 'Qui fait quoi',                        access: 'free', tier: 'advanced' },
  { to: '/tools/raci',      label: 'Responsabilités', shortLabel: 'RACI',      icon: '📊', tab: 'projets', primary: 'missions', description: 'Qui est responsable de quoi',          access: 'free', tier: 'advanced' },
  { to: '/tools/deal-room', label: 'Partage client',   shortLabel: 'Partage',   icon: '🤝', tab: 'projets', primary: 'documents', description: 'Présenter la mission au client',       access: 'starter', parcours: ['tech'], tier: 'advanced' },

  // ── Finance · Missions ────────────────────────────────────
  { to: '/tools/client-risk',          label: 'Évaluation client',   shortLabel: 'Évaluation',  icon: '⚠️', tab: 'finance', primary: 'opportunites', description: 'Évaluer un client avant de signer',             access: 'free',    section: 'Missions', parcours: ['tech', 'manuel', 'reglemente'], tier: 'advanced' },
  { to: '/tools/calc-mission',         label: 'Chiffrer une mission', shortLabel: 'Chiffrage',  icon: '🧮', tab: 'finance', primary: 'finances', description: 'Calculer ce que vous gagnez vraiment',          access: 'free',    section: 'Missions', parcours: ['tech', 'manuel', 'reglemente'] },
  { to: '/tools/calc-mission/goal',    label: 'Mon TJM',             shortLabel: 'Mon TJM',     icon: '🎯', tab: 'finance', primary: 'finances', description: 'Quel tarif viser pour atteindre mon objectif ?', access: 'free', section: 'Missions' },
  { to: '/tools/calc-mission/project', label: 'Projet forfait',      shortLabel: 'Forfait',     icon: '📦', tab: 'finance', primary: 'finances', description: 'Budget fixe donné ? Calculez si le projet est rentable', access: 'free', section: 'Missions', tier: 'advanced' },
  { to: '/tools/scratchpad',           label: 'Éditeur de formules', shortLabel: 'Formules',    icon: '📐', tab: 'finance', primary: 'finances', description: 'Posez vos calculs librement',                   access: 'starter', section: 'Missions', tier: 'advanced' },
  { to: '/tools/notes',                label: 'Notes',               shortLabel: 'Notes',       icon: '📝', tab: 'finance', primary: 'finances', description: 'Vos notes liées à vos missions',                access: 'starter', section: 'Missions', parcours: ['tech', 'manuel', 'reglemente'], tier: 'advanced' },
  { to: '/tools/tjm-radar',            label: 'Comparer au marché',  shortLabel: 'Marché',      icon: '📡', tab: 'finance', primary: 'finances', description: 'Comparez votre tarif au marché',                access: 'free',    section: 'Missions', parcours: ['tech', 'manuel', 'reglemente'] },
  { to: '/tools/missio',               label: 'Pilotage des missions', shortLabel: 'Pilotage',   icon: '🧭', tab: 'finance', primary: 'opportunites', description: 'Suivez vos missions et leur état de paiement',  access: 'starter', section: 'Missions', parcours: ['tech', 'manuel', 'reglemente'] },
  { to: '/tools/cra',                  label: 'Agenda',              shortLabel: 'Agenda',      icon: '📅', tab: 'finance', primary: 'missions', description: 'Notez vos jours travaillés chaque mois',        access: 'starter', section: 'Missions', parcours: ['tech'], tier: 'advanced' },
  { to: '/tools/chrono',               label: 'Chrono',              shortLabel: 'Chrono',      icon: '⏱', tab: 'finance', primary: 'missions', description: 'Relevé de vos prestations avec statut de paiement', access: 'starter', section: 'Missions', parcours: ['tech', 'manuel', 'reglemente'], tier: 'advanced' },
  { to: '/tools/mission-memory',       label: 'Retours missions',    shortLabel: 'Retours',     icon: '📓', tab: 'finance', primary: 'finances', description: 'Ce que vous avez appris de chaque mission',     access: 'starter', section: 'Missions', parcours: ['tech', 'manuel'], tier: 'advanced' },

  // ── Finance · Finances ────────────────────────────────────
  { to: '/tools/tresorerie',  label: 'Trésorerie',           shortLabel: 'Argent',      icon: '💰', tab: 'finance', primary: 'finances', description: 'Ce qui rentre et ce qui sort',                   access: 'starter', section: 'Finances', parcours: ['tech', 'manuel', 'reglemente'] },
  { to: '/tools/runway',      label: 'Réserve',              shortLabel: 'Réserve',     icon: '🚀', tab: 'finance', primary: 'finances', description: 'Combien de temps vous pouvez tenir sans revenus', access: 'starter', section: 'Finances', parcours: ['tech', 'manuel', 'reglemente'], tier: 'advanced' },
  { to: '/tools/projections', label: 'Projections',          shortLabel: 'Projections', icon: '📈', tab: 'finance', primary: 'finances', description: 'Estimez votre CA sur 12 mois, 3 scénarios',      access: 'pro',     section: 'Finances', parcours: ['tech', 'reglemente'], tier: 'advanced' },
  { to: '/tools/fiscal',      label: 'Optimisation fiscale', shortLabel: 'Fiscal',      icon: '🏛️', tab: 'finance', primary: 'finances', description: 'Quel statut vous rapporte le plus ?',            access: 'pro',     section: 'Finances', parcours: ['tech', 'reglemente'], tier: 'advanced' },
  { to: '/tools/benchmark',   label: 'Comparer au marché (détaillé)', shortLabel: 'Marché détaillé', icon: '📊', tab: 'finance', primary: 'finances', description: 'Version enrichie du comparateur marché, par secteur', access: 'pro',  section: 'Finances', parcours: ['tech'], tier: 'advanced' },
  { to: '/tools/reporting',   label: 'Reporting',            shortLabel: 'Reporting',   icon: '📈', tab: 'finance', primary: 'finances', description: 'Vos chiffres clés et vos meilleurs clients',     access: 'pro', section: 'Finances', tier: 'advanced' },

  // ── Finance · Commercial ─────────────────────────────────
  { to: '/tools/estimation', label: 'Chiffrage détaillé', shortLabel: 'Chiffrage détaillé', icon: '🎯', tab: 'finance', primary: 'documents', description: 'Estimer le temps et le coût par fonctionnalité avant de proposer',  access: 'starter', section: 'Commercial', tier: 'advanced' },
  { to: '/tools/proposal',   label: 'Proposition', shortLabel: 'Proposition', icon: '📝', tab: 'finance', primary: 'documents', description: 'Rédigez et envoyez une offre professionnelle',    access: 'starter', section: 'Commercial', parcours: ['tech', 'manuel', 'reglemente'] },
  { to: '/tools/devis',      label: 'Devis',       shortLabel: 'Devis',       icon: '📄', tab: 'finance', primary: 'documents', description: 'Créez et envoyez un devis en quelques minutes',  access: 'starter', section: 'Commercial', parcours: ['manuel', 'reglemente'], tier: 'advanced' },

  // ── Moi ──────────────────────────────────────────────────
  { to: '/tools/profile',        label: 'Profil',               shortLabel: 'Profil',       icon: '🪪', tab: 'moi', primary: 'profil', description: 'Vos informations professionnelles',          access: 'free' },
  { to: '/tools/stack-registry', label: "Référentiels d'outils", shortLabel: 'Référentiels', icon: '🗂', tab: 'moi', primary: 'profil', description: 'Inventaire de vos outils et compétences',    access: 'free', tier: 'advanced' },
  { to: '/tools/admin/licenses', label: 'Admin licences',        shortLabel: 'Licences',     icon: '🔐', tab: 'moi', primary: 'profil', description: 'Suivi interne des licences serveur',         access: 'owner' },
  { to: '/tools/admin/usage',    label: 'Usage admin',           shortLabel: 'Usage',        icon: '📈', tab: 'moi', primary: 'profil', description: 'Analyse des événements produit',            access: 'owner' },
];

const EXPERT_NAV_LINKS: Record<string, ExpertNavLink> = {
  '/missions': { to: '/missions', label: 'Missions', icon: '📁', description: 'Vue mission et accès direct au cadrage' },
};

const EXPERT_NAV_GROUP_DEFS: Array<{
  id: ExpertNavGroupId;
  label: string;
  icon: string;
  description: string;
  paths: string[];
}> = [
  {
    id: 'missions',
    label: 'Cadrage',
    icon: '📁',
    description: 'Préparer une mission et son périmètre',
    paths: ['/missions', '/tools/eval', '/tools/mission-canvas', '/tools/sipoc', '/tools/moscow', '/tools/raci', '/tools/client-risk', '/tools/missio'],
  },
  {
    id: 'pilotage',
    label: 'Pilotage',
    icon: '📊',
    description: 'TJM, trésorerie, benchmark et reporting',
    paths: ['/tools/calc-mission', '/tools/calc-mission/goal', '/tools/calc-mission/project', '/tools/tjm-radar', '/tools/tresorerie', '/tools/runway', '/tools/projections', '/tools/fiscal', '/tools/benchmark', '/tools/reporting', '/tools/chrono'],
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: '📄',
    description: 'Proposition, devis, partage et CRA',
    paths: ['/tools/proposal', '/tools/devis', '/tools/deal-room', '/tools/cra'],
  },
  {
    id: 'memoire',
    label: 'Mémoire',
    icon: '🧠',
    description: 'Notes, scratchpad et historique mission',
    paths: ['/tools/mission-memory', '/tools/scratchpad', '/tools/notes', '/tools/admin/licenses', '/tools/admin/usage'],
  },
];

export function getNavItem(pathname: string) {
  return HUB_NAV_ITEMS.find((item) => item.to === pathname);
}

export function getToolsForTab(tab: TabId): HubNavItem[] {
  return HUB_NAV_ITEMS.filter((item) => item.tab === tab && item.to !== `/${tab}`);
}

export function splitToolsByTier(tools: HubNavItem[]): { core: HubNavItem[]; advanced: HubNavItem[] } {
  const core: HubNavItem[] = [];
  const advanced: HubNavItem[] = [];
  for (const tool of tools) {
    if (tool.tier === 'advanced') advanced.push(tool);
    else core.push(tool);
  }
  return { core, advanced };
}

export function getExpertNavigationGroups(): ExpertNavGroup[] {
  return EXPERT_NAV_GROUP_DEFS.map((group) => ({
    id: group.id,
    label: group.label,
    icon: group.icon,
    description: group.description,
    items: group.paths
      .map((path) => EXPERT_NAV_LINKS[path] || HUB_NAV_ITEMS.find((item) => item.to === path))
      .filter((item): item is ExpertNavLink => Boolean(item))
      .map((item) => ({
        to: item.to,
        label: item.label,
        icon: item.icon,
        description: item.description,
        access: item.access,
      })),
  }));
}

export function getPrimaryNavigationItems() {
  return PRIMARY_NAV_ITEMS;
}

/**
 * Routes de premier niveau qui ne figurent pas au catalogue `HUB_NAV_ITEMS`
 * (ce sont des destinations, pas des "outils") : elles doivent être
 * déclarées ici explicitement plutôt que devinées.
 */
const STATIC_PRIMARY_ROUTES: Array<{ prefix: string; id: PrimaryNavId }> = [
  { prefix: '/missions/new', id: 'create' },
  { prefix: '/new-mission-wizard', id: 'create' },
  { prefix: '/new-mission', id: 'create' },
  { prefix: '/opportunites', id: 'opportunites' },
  { prefix: '/missions', id: 'missions' },
  { prefix: '/new-document', id: 'documents' },
  { prefix: '/documents', id: 'documents' },
];

/**
 * Résout la destination de navigation principale active pour une route
 * donnée. Entièrement dérivé de la configuration (`STATIC_PRIMARY_ROUTES`
 * pour les routes hors catalogue, `HUB_NAV_ITEMS[].primary` pour les
 * outils) — aucune heuristique ni chaîne de `startsWith` ad hoc (LOT 1,
 * remplace l'ancienne implémentation qui devinait la destination de
 * certains outils à partir de leur `intent`).
 */
export function getPrimaryNavigationActiveId(pathname: string): PrimaryNavId {
  if (pathname === '/') return 'accueil';

  const staticRoute = STATIC_PRIMARY_ROUTES.find(
    (route) => pathname === route.prefix || pathname.startsWith(`${route.prefix}/`),
  );
  if (staticRoute) return staticRoute.id;

  const exact = HUB_NAV_ITEMS.find((entry) => entry.to === pathname);
  if (exact) return exact.primary;

  const prefixMatches = HUB_NAV_ITEMS.filter((entry) => pathname.startsWith(`${entry.to}/`));
  const longestMatch = prefixMatches.sort((a, b) => b.to.length - a.to.length)[0];
  return longestMatch?.primary ?? 'accueil';
}

/**
 * Nombre d'outils distincts exposés, dérivé de la source unique du catalogue
 * (HUB_NAV_ITEMS). Les sous-routes d'un même outil (ex: /tools/calc-mission/goal)
 * ne sont comptées qu'une fois ; les entrées internes (admin) sont exclues.
 * Sert à remplacer tout compte figé affiché dans l'interface.
 */
export function countTools(): number {
  const roots = new Set<string>();
  for (const item of HUB_NAV_ITEMS) {
    const m = item.to.match(/^\/tools\/([a-z0-9-]+)/);
    if (m && m[1] !== 'admin') roots.add(m[1]);
  }
  return roots.size;
}

/**
 * Nombre d'outils distincts pertinents pour un parcours donné (dérivé du
 * catalogue, jamais figé). Exclut expressément les outils sans ce parcours et
 * les entrées admin. Évite tout compteur contradictoire dans l'onboarding.
 */
export function countToolsForParcours(parcours: 'tech' | 'manuel' | 'reglemente'): number {
  const roots = new Set<string>();
  for (const item of HUB_NAV_ITEMS) {
    if (!item.parcours?.includes(parcours)) continue;
    const m = item.to.match(/^\/tools\/([a-z0-9-]+)/);
    if (m && m[1] !== 'admin') roots.add(m[1]);
  }
  return roots.size;
}
